<template>
  <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <div
      v-for="(item, index) in statsDataList"
      :key="index"
      class="bg-white p-4 rounded shadow-md flex flex-col items-center justify-center"
    >
      <!-- Custom Canvas Circular Progress -->
      <div class="relative w-32 h-32 flex items-center justify-center">
        <canvas
          :ref="el => setCanvasRef(el, index)"
          width="96"
          height="96"
          class="w-32 h-32"
        ></canvas>
        <!-- Value text in center -->
        <div class="absolute inset-0 flex items-center justify-center">
          <span class="text-lg font-bold text-gray-800">{{ item.value }}</span>
        </div>
      </div>
      <!-- Label below chart -->
      <div class="mt-3 text-center text-sm font-medium text-gray-700">
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, watch, onMounted } from 'vue'

const props = defineProps({
  usersStats: {
    type: Object,
    default: () => ({})
  }
})

// Canvas refs
const canvasRefs = ref([])

// Computed stats data for charts
const statsDataList = computed(() => {
  const stats = props.usersStats
  const totalUsers = stats.all_user_organisation_statistic || 0
  
  return [
    {
      label: 'Total des utilisateurs',
      value: totalUsers,
      percentage: 100, // Always 100% for total
      color: '#00C2CB'
    },
    {
      label: 'Utilisateurs actifs',
      value: stats.total_user_accept_invitation || 0,
      percentage: totalUsers > 0 ? Math.round(((stats.total_user_accept_invitation || 0) / totalUsers) * 100) : 0,
      color: '#66BB6A'
    },
    {
      label: 'Invitations en attente',
      value: stats.total_user_refused_invitation || 0,
      percentage: totalUsers > 0 ? Math.round(((stats.total_user_refused_invitation || 0) / totalUsers) * 100) : 0,
      color: '#FFA726'
    },
    {
      label: 'Utilisateurs désactivés',
      value: stats.total_user_deactivated || 0,
      percentage: totalUsers > 0 ? Math.round(((stats.total_user_deactivated || 0) / totalUsers) * 100) : 0,
      color: '#EF5350'
    }
  ]
})

// Canvas methods
const setCanvasRef = (el, index) => {
  if (el) {
    canvasRefs.value[index] = el
    drawCircularProgress(el, statsDataList.value[index])
  }
}

const drawCircularProgress = (canvas, data) => {
  const ctx = canvas.getContext('2d')
  const centerX = canvas.width / 2
  const centerY = canvas.height / 2
  const radius = 35
  const lineWidth = 6
  
  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  
  // Draw background circle
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
  ctx.strokeStyle = '#F3F4F6'
  ctx.lineWidth = lineWidth
  ctx.stroke()
  
  // Draw progress arc
  const startAngle = -Math.PI / 2 // Start from top
  const endAngle = startAngle + (2 * Math.PI * data.percentage / 100)
  
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, startAngle, endAngle)
  ctx.strokeStyle = data.color
  ctx.lineWidth = lineWidth
  ctx.lineCap = 'round'
  ctx.stroke()
}

// Watch for data changes to redraw charts
watch(statsDataList, (newData) => {
  newData.forEach((item, index) => {
    if (canvasRefs.value[index]) {
      drawCircularProgress(canvasRefs.value[index], item)
    }
  })
}, { deep: true })

// Redraw charts on mount
onMounted(() => {
  setTimeout(() => {
    statsDataList.value.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item)
      }
    })
  }, 100)
})
</script>

<style scoped>
.primary-text {
  color: #1f2937;
}
</style>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss";
@use "../../assets/scss/app/common.scss";
</style>